import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { BsDisplay, BsPatchCheckFill } from 'react-icons/bs';
import { FiThumbsUp, FiClock, FiStar, FiLock } from 'react-icons/fi';
import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import bannerGif from '../../../../assets/images/banner.gif';
import { getServers, voteServer } from '../../../../store/servers/serversSlice';
import {
  PageWrapper, PageHeader, AdBanner,
  ServerList, ServerCard,
  RANK_COLORS, TYPE_STYLES,
} from './TopListStyle';
import { backendUrl } from "../../../../utilities/axios";
import { SkeletonListRow, NoData } from '../../../shared/WebsiteFeedback';


const TopList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast({ position: 'top', duration: 3000 });
  const { data: serversData, isLoading } = useSelector(state => state.servers);
  const auth = useSelector(state => state.auth);
  const [optimisticVotes, setOptimisticVotes] = useState({});
  const userId = auth.user?._id;

  useEffect(() => {
    dispatch(getServers({ page: 1, size: 20, query: '', filter: JSON.stringify({ is_active: true }), sort: '-votes' }));
  }, [dispatch]);

  const servers = serversData?.data || [];

  const isVoted = (srv) =>
    !!optimisticVotes[srv._id] ||
    !!srv.likedBy?.some(id => id === userId);

  const handleVote = (e, server) => {
    e.preventDefault();
    e.stopPropagation();
    if (!auth.user) {
      toast({ description: 'يجب تسجيل الدخول أولاً للإعجاب بالسيرفر', status: 'warning' });
      navigate('/login');
      return;
    }
    if (isVoted(server)) return;
    setOptimisticVotes(p => ({ ...p, [server._id]: true }));
    dispatch(voteServer(server)).unwrap().catch(err => {
      if (err?.errors?.[0]?.msg === 'already_liked') return;
      setOptimisticVotes(p => { const n = { ...p }; delete n[server._id]; return n; });
      toast({ description: 'حدث خطأ، حاول مرة أخرى', status: 'error' });
    });
  };

  const getImage = (server) => server.image ? `${backendUrl}/servers/${server.image}` : bannerGif;
  const getName  = (srv) =>
    i18n.language === 'ar'
      ? (srv.name_ar || srv.name_en || srv.name || '')
      : (srv.name_en || srv.name_ar || srv.name || '');

  const TYPE_LABEL_MAP = {
    oldschool: 'OLDSCHOOL',
    newschool: 'NEWSCHOOL',
    middleschool: 'MIDDLESCHOOL',
    custom: 'CUSTOM',
  };

  return (
    <WebsiteLayout>
      <PageWrapper>
        <PageHeader>
          <div className="page-title">
            <BsDisplay /> {t('website.toplist.title')}
          </div>
        </PageHeader>

        {isLoading ? (
          <ServerList>
            {Array.from({ length: 10 }, (_, i) => <SkeletonListRow key={i} />)}
          </ServerList>
        ) : servers.length === 0 ? (
          <NoData />
        ) : (
        <ServerList>
          {servers.map((srv, idx) => {
            const rank = idx + 1;
            const rc = RANK_COLORS[rank];
            const ts = TYPE_STYLES[srv.type] || TYPE_STYLES.custom;
            const voted = isVoted(srv);
            const typeLabel = TYPE_LABEL_MAP[srv.type] || 'CUSTOM';

            return (
              <ServerCard key={srv._id} as={Link} to={`/server/${srv._id}`}>
                <div className="card-header">
                  <div className="rank-badge">
                    {rc
                      ? <><span className="rank-medal">{rc.medal}</span><span className="rank-num" style={{ color: rc.num }}>{rank}</span></>
                      : <span className="rank-num" style={{ color: '#4b5563' }}>#{rank}</span>
                    }
                  </div>
                  <div className="server-name">{getName(srv)}</div>
                </div>

                <div className="card-body">
                  <div className="info-col">
                    {/* <div className="srv-title">{getName(srv)}</div> */}
                    <div className="badges">
                      <span className="verified-badge"><BsPatchCheckFill /> {t('website.toplist.verified')}</span>
                      <span className="type-badge"
                        style={{ background: ts.bg, color: ts.color, border: `1px solid ${ts.border}` }}>
                        {typeLabel}
                      </span>
                    </div>
                    <div className="stats">
                      <span className="stat">
                        <FiStar />
                        <span className="s-label">{t('website.toplist.max_lv_label')}</span>
                        <span className="s-val">{srv.level}</span>
                      </span>
                      <span className="stat">
                        <FiClock />
                        <span className="s-label">{t('website.toplist.online_label')}</span>
                        <span className="s-val">{srv.launch_date ? new Date(srv.launch_date).getFullYear() : '-'}</span>
                      </span>
                    </div>
                  </div>

                  <div className="img-col">
                    <img src={getImage(srv)} alt={srv.name} />
                  </div>

                  <div className="vote-col">
                    <button
                      className={`vote-btn${voted ? ' voted' : ''}${!auth.user ? ' locked' : ''}`}
                      onClick={e => handleVote(e, srv)}
                      title={!auth.user ? 'سجّل دخولك للإعجاب' : voted ? 'أعجبك هذا السيرفر' : 'أعجبني'}
                    >
                      {!auth.user ? <FiLock /> : <FiThumbsUp />}
                      <span className="count">{srv.votes.toLocaleString()}</span>
                    </button>
                  </div>
                </div>
              </ServerCard>
            );
          })}
        </ServerList>
        )}
      </PageWrapper>
    </WebsiteLayout>
  );
};

export default TopList;
